import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, ChevronLeft, ChevronRight, FolderOpen } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  image_url: string;
  published_at: string;
  blog_categories?: {
    name: string;
    slug: string;
  };
}

interface BlogTimelineProps {
  className?: string;
}

export default function BlogTimeline({ className = '' }: BlogTimelineProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchFeaturedPosts() {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('blog_posts')
          .select(`
            id, 
            title, 
            slug, 
            image_url, 
            published_at,
            blog_categories (
              name,
              slug
            )
          `)
          .eq('is_published', true)
          .eq('deleted', false)
          .order('published_at', { ascending: false })
          .limit(10);

        if (error) throw error;
        setFeaturedPosts(data || []);
      } catch (err) {
        console.error('Error fetching featured posts:', err);
        setError('Failed to load featured posts');
      } finally {
        setLoading(false);
      }
    }

    fetchFeaturedPosts();
  }, []);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    
    const scrollAmount = 300;
    const currentScroll = scrollContainerRef.current.scrollLeft;
    const newScroll = direction === 'left' 
      ? currentScroll - scrollAmount 
      : currentScroll + scrollAmount;
    
    scrollContainerRef.current.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className={`relative py-12 ${className}`}>
      {/* Navigation Buttons */}
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md hover:bg-gray-50 transition-colors duration-200"
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-6 w-6 text-gray-600" />
        </button>
      )}
      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md hover:bg-gray-50 transition-colors duration-200"
          aria-label="Scroll right"
        >
          <ChevronRight className="h-6 w-6 text-gray-600" />
        </button>
      )}

      {/* Timeline container */}
      <div 
        className="relative overflow-hidden"
        style={{ margin: '0 40px' }}
      >
        {/* Timeline line */}
        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200"></div>

        {/* Posts container */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="relative flex items-center gap-8 overflow-x-auto pb-4 scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {featuredPosts.map((post) => (
            <div key={post.id} className="relative flex flex-col items-center">
              {/* Date marker */}
              <div className="mb-4 bg-white px-3 py-1 rounded-full border border-gray-200 shadow-sm">
                <time className="text-sm font-medium text-gray-900 flex items-center">
                  <Calendar className="h-4 w-4 mr-1 text-[#F98B3D]" />
                  {new Date(post.published_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                </time>
              </div>

              {/* Timeline dot */}
              <div className="w-3 h-3 rounded-full bg-[#F98B3D] border-2 border-white shadow-sm mb-4"></div>

              {/* Content card */}
              <Link 
                to={`/blog/${post.slug}`}
                className="w-64 flex-shrink-0 group"
              >
                <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                  <div className="h-32 overflow-hidden">
                    {post.image_url && (
                      <img 
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-200"
                      />
                    )}
                  </div>
                  <div className="p-3">
                    {post.blog_categories && (
                      <div className="flex items-center mb-2">
                        <FolderOpen className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-600">
                          {post.blog_categories.name}
                        </span>
                      </div>
                    )}
                    <h2 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2 group-hover:text-[#F98B3D] transition-colors duration-200">
                      {post.title}
                    </h2>
                    <span className="inline-flex items-center text-xs text-[#F98B3D] group-hover:text-[#e07a2c]">
                      Read more
                      <ArrowRight className="h-3 w-3 ml-1 transform group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}